// @ts-strict-ignore
import { Dispatch, SetStateAction } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from '@node-space/ui-components'
import { usePutUpdateMassCryptoPayoutMutation } from 'hooks/mutations/usePutUpdateMassCryptoPayoutMutation'
import { useCryptoCurrenciesQuery } from 'hooks/queries/useCryptoCurrenciesQuery'
import {
  contactActionAtom,
  contactAtom,
  contactInstrumentActionAtom,
  contactInstrumentAtom,
  editingContactInstrumentAtom,
  massPayoutContactActionAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { getRequiredContactFieldsForCryptoAddress } from 'pages/Contacts/utils/fiatInstruments'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { ContactActionIds, ContactInstrumentActionIds } from 'types/atoms/atom.contacts'
import { MassPayoutActionIds } from 'types/atoms/atom.massPayouts'
import { BaseErrorResponse } from 'types/beneficiaries'
import { Contact, ContactInstrument, InstrumentType } from 'types/contacts'
import { logSentryErrorAndGetTraceId } from 'utils/sentry'
import { CryptoPayout } from '../types'
import { getNetworkByCurrencyAndProtocol } from '../utils'

interface TriggerContactSearchProps {
  setIsEditPayoutModalVisible: Dispatch<SetStateAction<boolean>>
  setIsWalletSheetOpen: Dispatch<SetStateAction<boolean>>
  setIsContactInstrumentSheetOpen: Dispatch<SetStateAction<boolean>>
  setIsMassPayoutsSheetsOpen: Dispatch<SetStateAction<boolean>>
  cryptoRecordToUpdate: CryptoPayout
  clearCryptoErrors: (input: string) => void
  setIsContactSheetOpen: Dispatch<SetStateAction<boolean>>
}

export const useTriggerContactSearch = ({
  setIsEditPayoutModalVisible,
  setIsWalletSheetOpen,
  setIsContactInstrumentSheetOpen,
  setIsMassPayoutsSheetsOpen,
  cryptoRecordToUpdate,
  clearCryptoErrors,
  setIsContactSheetOpen,
}: TriggerContactSearchProps) => {
  const queryClient = useQueryClient()
  const { reference: batchId } = useParams()
  const { t } = useTranslation()

  const setContactAction = useSetAtom(contactActionAtom)
  const [massPayoutContactAction, setMassPayoutContactAction] = useAtom(massPayoutContactActionAtom)
  const setContact = useSetAtom(contactAtom)
  const setContactInstrument = useSetAtom(contactInstrumentAtom)
  const setIsEditingContactInstrument = useSetAtom(editingContactInstrumentAtom)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)

  const { mutate: mutateUpdateCryptoRecord } = usePutUpdateMassCryptoPayoutMutation()
  const { data: cryptoCurrencies } = useCryptoCurrenciesQuery()

  const onhandleContactInstrumentUpdate = (contact: Contact) => {
    const payload = {
      batchId,
      externalId: cryptoRecordToUpdate?.externalId,
      fundingWalletAmount: cryptoRecordToUpdate?.fundingWalletAmount?.toString(),
      paidCurrencyAmount: cryptoRecordToUpdate?.paidCurrencyAmount?.toString(),
      currency: cryptoRecordToUpdate?.currency,
      network: cryptoRecordToUpdate?.network,
      protocol: cryptoRecordToUpdate?.protocol,
      contactId: contact?.id,
      address: cryptoRecordToUpdate?.address,
      reference: cryptoRecordToUpdate?.reference,
      tag: cryptoRecordToUpdate?.tag || '',
    }
    mutateUpdateCryptoRecord(payload, {
      onSuccess: () => {
        setIsWalletSheetOpen(false)
        queryClient.invalidateQueries({ queryKey: reactQueryKeys.massCryptoPayoutById(batchId) })
        clearCryptoErrors('travelRule')
      },
      onError: (error: BaseErrorResponse) => {
        logSentryErrorAndGetTraceId(
          `Failed API request to /v1/pay/masspayout/${batchId}/items/${cryptoRecordToUpdate?.externalId} with status ${error?.status} - putUpdateCryptoPayoutById`,
          error,
          {
            data: payload,
          }
        )
        toast({
          header: t('contacts.contactCouldNotBeAssigned'),
          body: t('contacts.contactAssignTechnicalError'),
          variant: 'error',
        })
      },
    })
  }

  const onHandleContactSelect = (contact: Contact) => {
    setContact(contact)

    const requiredFields = getRequiredContactFieldsForCryptoAddress(contact)

    if (requiredFields.length) {
      setContactAction({
        actionId: ContactActionIds.CONTACT,
        header: t('contacts.updateContact'),
        onClose: () => {
          queryClient.invalidateQueries({ queryKey: ['contacts'] })
          queryClient.refetchQueries({ queryKey: [reactQueryKeys.massCryptoPayoutById(batchId)] })
          setIsContactSheetOpen(false)
        },
        meta: {
          keepUpdatedContact: true,
          flowType: InstrumentType.CRYPTO,
          contactRequiredFields: {
            requiredFields,
            calloutMessage: t('contacts.cryptoAccountRequiredAdditionalContactDetailsMassPayout'),
          },
        },
      })
      setIsContactSheetOpen(true)
      return
    }

    const contactInstrument = {
      description: cryptoRecordToUpdate?.reference,
      currency: cryptoRecordToUpdate?.currency,
      network:
        cryptoRecordToUpdate?.network ??
        getNetworkByCurrencyAndProtocol(
          cryptoRecordToUpdate?.currency,
          cryptoRecordToUpdate?.protocol,
          cryptoCurrencies
        ),
      address: cryptoRecordToUpdate?.address,
      type: InstrumentType.CRYPTO,
    } as ContactInstrument

    if (!cryptoRecordToUpdate.contactId?.some(id => id?.contactId === contact?.id)) {
      setContactInstrument(contactInstrument)
      setIsEditingContactInstrument(false)
      setContactInstrumentAction({
        ...contactInstrumentAction,
        actionId: ContactInstrumentActionIds?.INSTRUMENT,
        header: t('contacts.addCryptoAccount'),
        onClose: () => {
          setIsContactInstrumentSheetOpen(false)
          setContact(null)
          setContactInstrument(null)
        },
        meta: {
          enableCreateInstrument: true,
          handleContactInstrumentUpdate: () => {
            onhandleContactInstrumentUpdate(contact)
          },
        },
      })
      setIsContactInstrumentSheetOpen(true)
    } else {
      onhandleContactInstrumentUpdate(contact)
    }
  }

  const triggerContactSearch = () => {
    const contactInstrument = {
      description: cryptoRecordToUpdate?.reference,
      currency: cryptoRecordToUpdate?.currency,
      network:
        cryptoRecordToUpdate?.network ??
        getNetworkByCurrencyAndProtocol(
          cryptoRecordToUpdate?.currency,
          cryptoRecordToUpdate?.protocol,
          cryptoCurrencies
        ),
      address: cryptoRecordToUpdate?.address,
      type: InstrumentType.CRYPTO,
    } as ContactInstrument
    setIsEditPayoutModalVisible(false)

    setMassPayoutContactAction({
      ...massPayoutContactAction,
      actionId: MassPayoutActionIds.MASSPAYOUTS,
      header: t('wallets.send.contactStepHeader'),
      onClose: () => {
        setIsMassPayoutsSheetsOpen(false)
      },
      meta: {
        enableCreateInstrument: true,
        flowType: InstrumentType.CRYPTO,
        handleContactSelect: contact => {
          onHandleContactSelect(contact)
        },
        ...(cryptoRecordToUpdate?.contactId && {
          contactFilter: cryptoRecordToUpdate?.contactId?.map(id => id?.contactId),
        }),
        contactInstrument: contactInstrument,
        handleContactInstrumentUpdate: (contactInstrument, contact) => {
          onhandleContactInstrumentUpdate(contact)
          setIsWalletSheetOpen(false)
        },
      },
    })
    setIsMassPayoutsSheetsOpen(true)
  }

  return {
    triggerContactSearch,
  }
}
